// Mantine
import {
    Modal,
    Menu,
    Anchor,
    ActionIcon,
    Button,
    Select,
    Loader,
    TextInput,
} from '@mantine/core'

import { useDisclosure } from '@mantine/hooks'
import { notifications } from '@mantine/notifications'
import { modals } from '@mantine/modals'

// Image compression
import Compressor from 'compressorjs'

// UI
import Box from './Box'

// React
import { useState, useRef } from 'react'

// Supabase
import { useAxiosApi } from '@/hooks/useAxiosApi'

// Dayjs
import dayjs from 'dayjs'


// User permission
import useMe from '@/hooks/useMe'

// Icons
import {
    IconFileFilled,
    IconPdf,
    IconFileTypePdf,
    IconDotsVertical,
    IconUpload,
    IconVideo,
} from '@tabler/icons-react'

// Lodash
import { sortBy } from 'lodash'

const categories = [
    {
        label: 'Commande fournisseur',
        description:
            'Devis, facture, bons de commande, colisage, etc. donnés par un fournisseur',
        slug: 'supplier_order',
        models: ['purchase_order'],
    },
    {
        label: 'Photo produit',
        description: 'Packshot qui sera affiché en gros',
        slug: 'product_photo',
        models: ['component'],
    },
    {
        label: "Instructions d'assemblage",
        description: "Instructions d'assemblage",
        slug: 'assembly_instructions',
        models: ['component'],
    },
    {
        label: 'Photo client',
        description: 'Envoyée par le client avec sa demande de réparation',
        slug: 'customer_photo',
        models: ['service_case'],
    },
    {
        label: 'Fichier divers',
        description: 'Ne rentre dans aucune autre catégorie',
        slug: 'misc',
    },
]

export default function Attachments({
    className,
    attachments,
    model_type,
    model_id,
    refetchModelData,
}) {
    // API
    const { requestPost, requestPatch, requestDelete } = useAxiosApi()

    // User permissions
    const { isAdmin } = useMe()

    // File preview modal
    const [previewingFile, setPreviewingFile] = useState(null)

    // Sort attachments by creation date
    const sortedAttachments = sortBy(attachments, 'created_at').reverse()

    // Available categories
    const availableCategories = categories.filter(
        (category) =>
            category.models?.includes(model_type) ||
            category?.models === undefined
    )

    const categoryOptions = availableCategories.map((category) => ({
        label: category.label,
        value: category.slug,
    }))

    /*
        UPLOAD
    */

    // Modal
    const [opened, handlers] = useDisclosure(false)

    // Upload states
    const [isUploading, setIsUploading] = useState(false)

    // File name
    const [fileNameValue, setFileNameValue] = useState('')
    const [categoryValue, setCategoryValue] = useState('')
    const [uploadedFile, setUploadedFile] = useState(null)

    // File input
    const fileInputRef = useRef()

    const open = () => {
        if (!isAdmin) {
            return
        }

        setIsUploading(false)
        setUploadedFile(null)
        setFileNameValue('')
        setCategoryValue(availableCategories[0]?.slug)
        handlers.open()
    }

    const openFileInput = () => {
        fileInputRef.current.click()
    }

    // Send a file to the API, which stores it in the bucket
    const doUpload = async (fileToUpload, originalFile) => {
        const fileName = originalFile.name
        const patternFileExtension = /\.([0-9a-z]+)(?:[\?#]|$)/i
        const filenameWithoutExtension = fileName.replace(patternFileExtension, '')

        if (!fileNameValue) {
            setFileNameValue(filenameWithoutExtension)
        }

        setIsUploading(true)

        try {
            // The bytes go to the API, which puts them in the bucket. The name
            // the file ends up with is the server's to choose.
            const form = new FormData()
            form.append('file', fileToUpload, originalFile.name)
            form.append('bucket', 'attachements')

            const { data } = await requestPost('/uploads', form)

            setUploadedFile(data)
        } finally {
            setIsUploading(false)
        }
    }

    // Compress images and upload file
    const uploadFile = async (file) => {
        fileInputRef.current.value = null

        if (file.size > 104857600) {
            notifications.show({
                title: 'Fichier trop lourd',
                message: 'La pièce jointe ne peut pas dépasser 100 Mo',
                color: 'red',
            })
            return
        }

        // Compress images before upload (converts HEIC/HEIF to JPEG on supported browsers).
        // Les PNG restent en PNG : les convertir en JPEG détruit la transparence
        // (le fond alpha est rempli en blanc), or les packshots produits en dépendent.
        if (file.type.startsWith('image/')) {
            const keepPng = file.type === 'image/png'
            const outputType = keepPng ? 'image/png' : 'image/jpeg'

            new Compressor(file, {
                maxWidth: 1920,
                maxHeight: 1920,
                quality: 0.8,
                mimeType: outputType,
                // CompressorJS reconvertit en JPEG tout type listé dans convertTypes
                // (défaut : ['image/png']) dont la taille dépasse convertSize. Avec 0,
                // ça écrasait le mimeType ci-dessus. Infinity désactive ce comportement.
                convertSize: keepPng ? Infinity : 0,
                success: (compressedFile) => {
                    // Override the original file reference so the stored type matches the output
                    const converted = new File(
                        [compressedFile],
                        file.name.replace(/\.\w+$/, keepPng ? '.png' : '.jpg'),
                        { type: outputType }
                    )
                    doUpload(converted, converted)
                },
                error: () => doUpload(file, file),
            })
        } else {
            doUpload(file, file)
        }
    }

    const attachFile = async () => {
        if (uploadedFile && fileNameValue) {
            await requestPost('/attachments', {
                name: fileNameValue,
                file: uploadedFile.file,
                type: uploadedFile.type,
                category: categoryValue,
                model_type,
                model_id,
            })

            notifications.show({
                title: 'Fichier ajouté',
                message: 'Le fichier a bien été ajouté',
                color: 'green',
            })

            refetchModelData()
            handlers.close()
        }
    }

    const deleteAttachment = async (attachmentToDelete) => {
        const confirmDelete = async () => {
            await requestDelete(`/attachments/${attachmentToDelete.id}`)

            notifications.show({
                title: 'Fichier supprimé',
                message: 'Le fichier a bien été supprimé',
                color: 'green',
            })

            refetchModelData()
        }

        // Confirm deletion
        modals.openConfirmModal({
            title: 'Êtes-vous sûr de supprimer ce fichier ?',
            children: (
                <span>
                    Le fichier{' '}
                    <span className="font-medium">
                        {attachmentToDelete.name}
                    </span>{' '}
                    sera définitivement supprimé.
                </span>
            ),
            labels: { confirm: 'Supprimer le fichier', cancel: 'Annuler' },
            onConfirm: confirmDelete,
            confirmProps: { color: 'red' },
        })
    }

    // Change attachement name or category
    const [editingAttchmentId, setEditingAttchmentId] = useState(null)

    const openEditingAttachment = (attachment) => {
        setEditingAttchmentId(attachment.id)

        setFileNameValue(attachment.name)
        setCategoryValue(attachment.category)
    }

    const editAttachment = async () => {
        if (editingAttchmentId && fileNameValue && categoryValue) {
            await requestPatch(`/attachments/${editingAttchmentId}`, {
                name: fileNameValue,
                category: categoryValue,
            })

            notifications.show({
                title: 'Fichier modifié',
                message: 'Le fichier a bien été modifié',
                color: 'green',
            })

            refetchModelData()
            setEditingAttchmentId(null)
        }
    }

    return (
        <>
            <Box
                title="Fichiers"
                subtitle={attachments.length + ' fichier(s)'}
                rightSection={
                    isAdmin && (
                        <Button variant="default" size="xs" onClick={open}>
                            <IconUpload size="1rem" />
                        </Button>
                    )
                }
            >
                {attachments.length === 0 && (
                    <div>
                        <span className="text-gray-500">Aucun fichier</span>
                    </div>
                )}

                <div className="flex flex-col gap-3">
                    {sortedAttachments.map((attachment) => {
                        const fileExtension = attachment.file.split('.').pop()
                        const fileUrl = `https://wtlltffvwmgvkdwvakua.supabase.co/storage/v1/object/public/attachements/${attachment.file}`

                        const category = availableCategories.find(
                            (category) => category.slug === attachment.category
                        )

                        let FileThumbnail = (
                            <div className="w-[50px] aspect-square rounded-lg bg-gray-100 flex items-center justify-center shrink-0">
                                <IconFileFilled size={20} color="gray" />
                            </div>
                        )

                        if (attachment.type === 'application/pdf') {
                            FileThumbnail = (
                                <div className="w-[50px] aspect-square rounded-lg bg-gray-100 flex items-center justify-center shrink-0">
                                    <IconPdf size={20} color="black" />
                                </div>
                            )
                        }

                        if (attachment.type.includes('image')) {
                            FileThumbnail = (
                                <div className="w-[50px] aspect-square rounded-lg bg-gray-100 overflow-hidden shrink-0">
                                    <img
                                        src={`https://baltic-supabase.b-cdn.net/public/attachements/${attachment.file}?width=200`}
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                            )
                        }

                        if (attachment.type.includes('video')) {
                            FileThumbnail = (
                                <div className="w-[50px] aspect-square rounded-lg bg-gray-100 flex items-center justify-center shrink-0">
                                    <IconVideo size={20} color="black" />
                                </div>
                            )
                        }

                        const canPreview = attachment.type.includes('video') || attachment.type.includes('image')

                        const handleClick = () => {
                            if (canPreview) {
                                setPreviewingFile(attachment)
                            } else {
                                window.open(fileUrl, '_blank')
                            }
                        }

                        return (
                            <div
                                className="flex w-full justify-between items-center border p-3 rounded-xl relative cursor-pointer hover:bg-gray-50 group"
                                key={attachment.id}
                                onClick={handleClick}
                            >
                                <div className="flex items-center gap-3 truncate">
                                    {FileThumbnail}

                                    <div className="flex flex-col truncate">
                                        <span className="text-sm text-gray-400">
                                            {category?.label}
                                        </span>

                                        <div className="truncate text-baltic mt-1">
                                            <Anchor>
                                                {attachment.name
                                                    ? attachment.name
                                                    : attachment.file}
                                            </Anchor>
                                        </div>

                                        <span className="text-sm text-gray-400">
                                            {'Ajouté le ' +
                                                dayjs(
                                                    attachment.created_at
                                                ).format(' ll')}
                                            {attachment.user?.name && ` par ${attachment.user.name}`}
                                        </span>
                                    </div>
                                </div>

                                <div
                                    className="flex hover:bg-transparent"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    {isAdmin && (
                                        <Menu position="bottom-end">
                                            <Menu.Target>
                                                <ActionIcon variant="default">
                                                    <IconDotsVertical size="1rem" />
                                                </ActionIcon>
                                            </Menu.Target>

                                            <Menu.Dropdown>
                                                <Menu.Item
                                                    onClick={() =>
                                                        openEditingAttachment(
                                                            attachment
                                                        )
                                                    }
                                                >
                                                    Modifier
                                                </Menu.Item>

                                                <Menu.Item
                                                    color="red"
                                                    onClick={() =>
                                                        deleteAttachment(
                                                            attachment
                                                        )
                                                    }
                                                >
                                                    Supprimer
                                                </Menu.Item>
                                            </Menu.Dropdown>
                                        </Menu>
                                    )}
                                </div>
                            </div>
                        )
                    })}
                </div>

                {isAdmin && (
                    <Button
                        variant="default"
                        className="mx-auto mt-5"
                        rightSection={<IconUpload size="1rem" />}
                        onClick={open}
                        fullWidth
                    >
                        <span className="ml-2">Ajouter un fichier</span>
                    </Button>
                )}
            </Box>

            <Modal
                opened={Boolean(previewingFile)}
                onClose={() => setPreviewingFile(null)}
                size="100%"
                title="Aperçu du fichier"
                xOffset="clamp(12px, 4vw, 64px)"
                yOffset="16px"
                centered
            >
                {previewingFile?.file && (
                    previewingFile?.type?.includes('video') ? (
                        <video
                            className="w-full max-h-[calc(98vh-150px)]"
                            src={
                                'https://wtlltffvwmgvkdwvakua.supabase.co/storage/v1/object/public/attachements/' +
                                previewingFile?.file
                            }
                            controls
                            autoPlay
                        />
                    ) : previewingFile.type?.includes('image') ? (
                        <div className="flex flex-col gap-3">
                            <div className="flex h-[calc(100dvh-160px)] items-center justify-center overflow-hidden rounded-lg bg-gray-100">
                                <img
                                    className="block h-full w-full object-contain"
                                    src={`https://wtlltffvwmgvkdwvakua.supabase.co/storage/v1/object/public/attachements/${previewingFile.file}`}
                                    alt={previewingFile.name || previewingFile.file}
                                />
                            </div>
                            <Anchor
                                className="self-end"
                                href={`https://wtlltffvwmgvkdwvakua.supabase.co/storage/v1/object/public/attachements/${previewingFile.file}`}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Ouvrir l’original
                            </Anchor>
                        </div>
                    ) : (
                        <iframe
                            className="w-full h-[calc(98vh-150px)]"
                            title={previewingFile.name || previewingFile.file}
                            src={
                                'https://wtlltffvwmgvkdwvakua.supabase.co/storage/v1/object/public/attachements/' +
                                previewingFile?.file
                            }
                        />
                    )
                )}
            </Modal>

            <Modal
                opened={opened}
                onClose={() => handlers.close()}
                size="md"
                title="Ajouter un fichier"
            >
                <div className="flex flex-col gap-3">
                    <div className="flex flex-col gap-1">
                        <span className="text-sm font-medium">Fichier</span>

                        {!uploadedFile && (
                            <button
                                type="button"
                                className="w-full aspect-video cursor-pointer border"
                                onClick={openFileInput}
                            >
                                <span className="text-gray-500 text-xs">
                                    {isUploading ? (
                                        <Loader />
                                    ) : (
                                        'Ajouter un fichier...'
                                    )}
                                </span>
                            </button>
                        )}

                        {uploadedFile && (
                            <div>
                                {uploadedFile.type.includes('image') && (
                                    <img
                                        className="w-full aspect-square border object-contain"
                                        src={`https://wtlltffvwmgvkdwvakua.supabase.co/storage/v1/object/public/attachements/${uploadedFile.file}`}
                                    />
                                )}

                                {uploadedFile.type.includes('video') && (
                                    <video
                                        className="w-full border"
                                        src={`https://wtlltffvwmgvkdwvakua.supabase.co/storage/v1/object/public/attachements/${uploadedFile.file}`}
                                        controls
                                    />
                                )}

                                {uploadedFile.type === 'application/pdf' && (
                                    <div className="w-full aspect-square border flex items-center justify-center bg-gray-50">
                                        <IconFileTypePdf
                                            className="text-gray-800"
                                            size={64}
                                        />
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    <input
                        type="file"
                        ref={fileInputRef}
                        className="hidden"
                        onChange={(e) => {
                            uploadFile(e.target.files[0])
                        }}
                    />

                    <TextInput
                        label="Nom"
                        value={fileNameValue}
                        onChange={(e) => setFileNameValue(e.target.value)}
                    />

                    <Select
                        label="Catégorie de fichier"
                        data={categoryOptions}
                        value={categoryValue}
                        onChange={(value) => setCategoryValue(value)}
                        allowDeselect={false}
                    />

                    <Button
                        disabled={
                            !(fileNameValue && uploadedFile && categoryValue)
                        }
                        onClick={attachFile}
                    >
                        Uploader le fichier
                    </Button>
                </div>
            </Modal>

            <Modal
                opened={editingAttchmentId}
                onClose={() => setEditingAttchmentId(null)}
                size="md"
                title="Modifier le fichier"
            >
                <div className="flex flex-col gap-3">
                    <TextInput
                        label="Nom"
                        value={fileNameValue}
                        onChange={(e) => setFileNameValue(e.target.value)}
                    />

                    <Select
                        label="Catégorie de fichier"
                        data={categoryOptions}
                        value={categoryValue}
                        onChange={(value) => setCategoryValue(value)}
                        allowDeselect={false}
                    />

                    <Button
                        disabled={!(fileNameValue && categoryValue)}
                        onClick={editAttachment}
                    >
                        Modifier
                    </Button>
                </div>
            </Modal>
        </>
    )
}
